'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useCadrinoCopy } from '@/lib/cadrino-landing/useCadrinoCopy';
import { easeOut } from '@/lib/animations';
import { Container, MotionBlock, Section, SectionHeader } from './shared';
import { cn } from '@/lib/utils';
type FaqItemProps = {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
};
function FaqItem(t0) {
  const $ = _c(23);
  const {
    question,
    answer,
    isOpen,
    onToggle
  } = t0;
  const reducedMotion = useReducedMotion();
  const t1 = isOpen && "bg-white/[0.04]";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 transition-colors duration-300", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== question) {
    t3 = <span>{question}</span>;
    $[2] = question;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isOpen && "rotate-180";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("h-4 w-4 shrink-0 text-cadrino-muted transition-transform duration-300 ease-out", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <ChevronDown className={t5} aria-hidden={true} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isOpen || $[9] !== onToggle || $[10] !== t3 || $[11] !== t6) {
    t7 = <button type="button" onClick={onToggle} aria-expanded={isOpen} className="flex w-full items-center justify-between gap-3 py-3 text-left text-sm font-medium text-slate-100">{t3}{t6}</button>;
    $[8] = isOpen;
    $[9] = onToggle;
    $[10] = t3;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== answer || $[14] !== isOpen || $[15] !== reducedMotion) {
    t8 = isOpen && <motion.div key="content" initial={reducedMotion ? {
      opacity: 0
    } : {
      height: 0,
      opacity: 0
    }} animate={reducedMotion ? {
      opacity: 1
    } : {
      height: "auto",
      opacity: 1
    }} exit={reducedMotion ? {
      opacity: 0
    } : {
      height: 0,
      opacity: 0
    }} transition={{
      duration: reducedMotion ? 0.15 : 0.28,
      ease: easeOut
    }} className="overflow-hidden"><p className="pb-4 text-sm leading-relaxed text-cadrino-muted">{answer}</p></motion.div>;
    $[13] = answer;
    $[14] = isOpen;
    $[15] = reducedMotion;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <AnimatePresence initial={false}>{t8}</AnimatePresence>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t2 || $[20] !== t7 || $[21] !== t9) {
    t10 = <div className={t2}>{t7}{t9}</div>;
    $[19] = t2;
    $[20] = t7;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
export function FaqSection() {
  const $ = _c(13);
  const c = useCadrinoCopy();
  const [openKeys, setOpenKeys] = useState(_temp);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = key => {
      setOpenKeys(prev => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const toggle = t0;
  let t1;
  if ($[1] !== c.faq.title) {
    t1 = <MotionBlock><SectionHeader title={c.faq.title} /></MotionBlock>;
    $[1] = c.faq.title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== c.faq.items || $[4] !== openKeys) {
    let t3;
    if ($[6] !== openKeys) {
      t3 = item => <FaqItem key={item.q} question={item.q} answer={item.a} isOpen={openKeys.has(item.q)} onToggle={() => toggle(item.q)} />;
      $[6] = openKeys;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    t2 = c.faq.items.map(t3);
    $[3] = c.faq.items;
    $[4] = openKeys;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[8] !== t2) {
    t3 = <MotionBlock className="mt-10 max-w-3xl space-y-2">{t2}</MotionBlock>;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t1 || $[11] !== t3) {
    t4 = <Section id="faq" className="border-t border-white/[0.08]"><Container>{t1}{t3}</Container></Section>;
    $[10] = t1;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function _temp() {
  return new Set();
}